import React from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiX, FiCheck, FiTarget, FiZap, FiTrendingUp } = FiIcons;

const PricingSection = () => {
  const withoutFunnelsDone = [
    { tool: "Mailchimp", price: "₹2,500" },
    { tool: "Google Forms", price: "₹800" },
    { tool: "Typeform", price: "₹3,500" },
    { tool: "Calendly", price: "₹1,200" },
    { tool: "ClickFunnels", price: "₹8,000" },
    { tool: "WebinarJam", price: "₹4,500" },
    { tool: "CRM Software", price: "₹5,000" },
    { tool: "WhatsApp Tools", price: "₹3,000" },
    { tool: "Video Hosting", price: "₹2,000" },
    { tool: "Doc Hosting", price: "₹1,500" },
    { tool: "Community Platform", price: "₹4,000" },
    { tool: "Analytics Tools", price: "₹2,500" }
  ];

  const withFunnelsDone = [
    "All tools in one place",
    "Unlimited funnels",
    "Unlimited contacts",
    "Unlimited users",
    "Unlimited video hosting",
    "AI + WhatsApp Automation",
    "Complete CRM System",
    "Webinar Platform",
    "Appointment Booking",
    "Community Platform",
    "E-commerce Integration",
    "24/7 Support & Training"
  ];

  const totalMonthly = withoutFunnelsDone.reduce((sum, item) => {
    return sum + parseInt(item.price.replace('₹', '').replace(',', ''));
  }, 0);

  return (
    <section className="py-20 bg-gradient-to-br from-gray-900 via-blue-900 to-purple-900 relative overflow-hidden">
      {/* Background Animation */}
      <div className="absolute inset-0">
        <div className="absolute top-10 left-10 w-72 h-72 bg-blue-500 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-pulse"></div>
        <div className="absolute top-40 right-10 w-72 h-72 bg-purple-500 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-pulse" style={{animationDelay: '2s'}}></div>
        <div className="absolute bottom-20 left-1/2 w-72 h-72 bg-pink-500 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-pulse" style={{animationDelay: '4s'}}></div>
      </div>

      <div className="container mx-auto px-6 relative z-10">
        <motion.div 
          className="text-center mb-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <h2 className="text-4xl lg:text-5xl font-bold text-white mb-6">
            More Powerful Than <span className="text-yellow-400">20 Different Tools</span> — 
            <br />At the Cost of One
          </h2>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            Stop paying multiple subscriptions. Get everything you need in one powerful platform.
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-12 max-w-7xl mx-auto">
          {/* Without FunnelsDone */}
          <motion.div 
            className="bg-red-900/20 backdrop-blur-lg border border-red-500/30 rounded-3xl p-8"
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <div className="text-center mb-8">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-red-500/20 rounded-full mb-4">
                <SafeIcon icon={FiX} className="w-8 h-8 text-red-400" />
              </div>
              <h3 className="text-3xl font-bold text-red-400 mb-2">Without FunnelsDone</h3>
              <p className="text-gray-300">Multiple expensive subscriptions</p>
            </div>

            <div className="space-y-3 mb-8">
              {withoutFunnelsDone.map((item, index) => (
                <motion.div
                  key={index}
                  className="flex items-center justify-between p-3 bg-red-900/30 rounded-lg border border-red-500/20"
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.3, delay: index * 0.05 }}
                  viewport={{ once: true }}
                >
                  <div className="flex items-center">
                    <SafeIcon icon={FiX} className="w-5 h-5 text-red-400 mr-3" />
                    <span className="text-gray-200">{item.tool}</span>
                  </div>
                  <span className="text-red-400 font-semibold">{item.price}/mo</span>
                </motion.div>
              ))}
            </div>

            <div className="border-t border-red-500/30 pt-6">
              <div className="flex justify-between items-center">
                <span className="text-xl font-bold text-white">Total Monthly Cost:</span>
                <span className="text-3xl font-bold text-red-400">₹{totalMonthly.toLocaleString()}</span>
              </div>
              <div className="text-center mt-4">
                <div className="text-lg text-gray-300">Annual Cost: <span className="text-red-400 font-bold">₹{(totalMonthly * 12).toLocaleString()}</span></div>
              </div>
            </div>
          </motion.div>

          {/* With FunnelsDone */}
          <motion.div 
            className="bg-green-900/20 backdrop-blur-lg border border-green-500/30 rounded-3xl p-8 relative"
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            {/* Popular Badge */}
            <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
              <div className="bg-gradient-to-r from-yellow-400 to-orange-500 text-black px-6 py-2 rounded-full font-bold text-sm">
                🏆 MOST POPULAR
              </div>
            </div>

            <div className="text-center mb-8 mt-4">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-green-500/20 rounded-full mb-4">
                <SafeIcon icon={FiCheck} className="w-8 h-8 text-green-400" />
              </div>
              <h3 className="text-3xl font-bold text-green-400 mb-2">With FunnelsDone</h3>
              <p className="text-gray-300">Everything in one platform</p>
            </div>

            <div className="space-y-3 mb-8">
              {withFunnelsDone.map((feature, index) => (
                <motion.div
                  key={index}
                  className="flex items-center p-3 bg-green-900/30 rounded-lg border border-green-500/20"
                  initial={{ opacity: 0, x: 20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.3, delay: index * 0.05 }}
                  viewport={{ once: true }}
                >
                  <SafeIcon icon={FiCheck} className="w-5 h-5 text-green-400 mr-3" />
                  <span className="text-gray-200">{feature}</span>
                </motion.div>
              ))}
            </div>

            <div className="border-t border-green-500/30 pt-6">
              <div className="text-center">
                <div className="text-lg text-gray-300 mb-2">Instead of ₹{totalMonthly.toLocaleString()}/month</div>
                <div className="text-4xl font-bold text-green-400 mb-4">One Low Price</div>
                <div className="bg-gradient-to-r from-yellow-500/20 to-orange-500/20 border border-yellow-500/30 rounded-xl p-4 mb-6">
                  <div className="text-2xl font-bold text-white">Save 80%+ Annually</div>
                  <div className="text-yellow-400">+ Unlimited everything</div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Value Proposition */}
        <motion.div 
          className="text-center mt-16 max-w-5xl mx-auto"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.3 }}
          viewport={{ once: true }}
        >
          <div className="bg-gradient-to-r from-yellow-500/10 to-orange-500/10 border border-yellow-500/30 rounded-3xl p-12 backdrop-blur-lg">
            <h3 className="text-3xl lg:text-4xl font-bold text-white mb-6">
              Grow Your MSME Business the <span className="text-yellow-400">Smart Way</span>
            </h3>
            
            <div className="grid md:grid-cols-3 gap-8 mb-8">
              <div className="text-center">
                <SafeIcon icon={FiZap} className="w-12 h-12 text-yellow-400 mx-auto mb-4" />
                <h4 className="text-xl font-bold text-white mb-2">Instant Setup</h4>
                <p className="text-gray-300">Get started in minutes, not months</p>
              </div>
              <div className="text-center">
                <SafeIcon icon={FiTarget} className="w-12 h-12 text-yellow-400 mx-auto mb-4" />
                <h4 className="text-xl font-bold text-white mb-2">Proven Results</h4>
                <p className="text-gray-300">500+ MSMEs already growing</p>
              </div>
              <div className="text-center">
                <SafeIcon icon={FiTrendingUp} className="w-12 h-12 text-yellow-400 mx-auto mb-4" />
                <h4 className="text-xl font-bold text-white mb-2">Scale Fast</h4>
                <p className="text-gray-300">300% average growth rate</p>
              </div>
            </div>

            <motion.div
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <a 
                href="https://funnelsdone.com/cash" 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-flex items-center px-10 py-5 bg-gradient-to-r from-yellow-500 to-orange-500 text-black font-bold text-xl rounded-full hover:from-yellow-400 hover:to-orange-400 transition-all duration-300 shadow-2xl hover:shadow-yellow-500/25"
              >
                👉 Get Your Custom Pricing & Demo
                <SafeIcon icon={FiTarget} className="ml-3 w-6 h-6" />
              </a>
            </motion.div>

            <p className="text-gray-300 mt-4 text-sm">
              💡 Book a free strategy call to see exact pricing for your business needs
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default PricingSection;